import React, { useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Plus } from "lucide-react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import Dialog from "@/src/components/dialog/Dialog";
import { Button } from "@/src/components/ui/button";
import { useOrganizationFontMutations } from "@/src/hooks/mutations/useOrganizationFontMutations";

import AddFontForm from "./AddFontForm";
import { addFontSchema } from "./addFontSchema";
import UploadedFontSummary from "./UploadedFontSummary";

import type { JSX } from "react";

import type { FontFamily } from "@/src/api/StylesAPI.types";

import type { AddFontFormValues } from "./addFontSchema";

interface AddFontDialogProps {
  onUploaded: () => void;
}

function AddFontDialog({ onUploaded }: AddFontDialogProps): JSX.Element {
  const { t } = useTranslation("editor-appearance");
  const { uploadFont, isUploading } = useOrganizationFontMutations();

  const [isOpen, setIsOpen] = useState(false);
  const [uploadedFamily, setUploadedFamily] = useState<FontFamily | null>(null);

  const methods = useForm<AddFontFormValues>({
    defaultValues: { fileList: [], isLicenseDeclared: false },
    mode: "onChange",
    resolver: zodResolver(addFontSchema),
  });

  const handleOpen = (): void => {
    methods.reset();
    setUploadedFamily(null);
    setIsOpen(true);
  };

  const handleClose = (): void => {
    setIsOpen(false);
  };

  const onValid = async ({ fileList }: AddFontFormValues): Promise<void> => {
    try {
      setUploadedFamily(await uploadFont(fileList));
      toast.success(t("uploadSuccess"));
      onUploaded();
    } catch {
      toast.error(t("uploadError"));
    }
  };

  const isConfirmDisabled = uploadedFamily
    ? false
    : !methods.formState.isValid || isUploading;

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <FormProvider {...methods}>
      <Button onClick={handleOpen} type="button" variant="outline">
        <Plus className="size-4" />
        {t("addFont")}
      </Button>
      <Dialog
        cancelLabel={t("cancel")}
        confirmLabel={uploadedFamily ? t("close") : t("uploadSubmit")}
        description={uploadedFamily ? undefined : t("addFontDescription")}
        isCancelHidden={uploadedFamily !== null}
        isConfirmDisabled={isConfirmDisabled}
        isOpen={isOpen}
        onConfirm={uploadedFamily ? handleClose : methods.handleSubmit(onValid)}
        onOpenChange={setIsOpen}
        title={t("addFont")}
      >
        {uploadedFamily ? (
          <UploadedFontSummary family={uploadedFamily} />
        ) : (
          <AddFontForm />
        )}
      </Dialog>
    </FormProvider>
  );
}

export default AddFontDialog;
